import * as Tone from "tone";
import drumKitManager from "./drumKitManager";
import bassKitManager from "./bassKitManager";
import melodyKitManager from "./melodyKitManager";
import { normalizeSampleUrl } from "../utils/normalizeSampleUrl";

// v9.9 — GenPlayer plays the /api/ai/generate interchange pattern with
// PER-COMPONENT sound assignments so what the producer hears in the
// Training Ground / landing-page playback matches 1:1 with what hydrates
// in the DAW when they hit "Edit in DAW".
//
// The pattern payload now carries a `components` map:
//   { kick, snare, hihat, clap, bass, melody } → { track, region, project_id }
// where track.sampleUrl / track.samplerRootPitch / track.samplerSourceKit
// identify the exact source-project sound to play for that role.
//
// Playback flow:
//   1) Pre-decode every component's sampleUrl to an AudioBuffer on the
//      online context (Tone.Sampler wants the buffer up-front — decoding
//      inside the transport callback would introduce latency + races).
//   2) Build one Tone.Sampler per component, keyed at its samplerRootPitch.
//   3) On each transport step, trigger the matching component sampler with
//      the region's exact notes/pattern.
//   4) Fall back to the admin's default drum/bass/melody kit ONLY when the
//      interchange left a component slot empty (cold-start scenario).

async function decodeSampleUrl(url) {
  if (!url) return null;
  // v18.5 — Heal legacy absolute-URL rows in curated_projects that were
  // saved with the preview host baked in, so guests on production
  // (crowdaw.com) actually hear the source 808/Hi-Hat instead of the
  // generic synth fallback that the previous cross-origin fetch left them
  // stuck on. Falls back to a no-op when the URL is already correct.
  const fetchUrl = normalizeSampleUrl(url);
  try {
    const res = await fetch(fetchUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const arr = await res.arrayBuffer();
    const ctx = Tone.getContext().rawContext;
    return await new Promise((resolve, reject) => {
      const maybe = ctx.decodeAudioData(arr, resolve, reject);
      if (maybe && typeof maybe.then === "function") maybe.then(resolve, reject);
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[decodeSampleUrl] failed to decode", fetchUrl, e?.message || e);
    return null;
  }
}

// v12.1 — Build a Tone.js effect node for the GenPlayer signal chain
// that mirrors AudioEngine.createFxNode + applyFxParams. This is how a
// saved DAW project's FX chain (Crow Pumper, Delay, Reverb, etc.) makes
// it into the /api/ai/generate playback — so a House pad with a 1/4
// note pumper sounds IDENTICAL between the DAW session it was authored
// in and the beat generator preview.
function buildGenFxNode(plugin, params = {}) {
  try {
    switch (plugin) {
      case "pumper": {
        // Rhythmic sidechain-style volume duck. Mirrors AudioEngine's
        // implementation exactly so the pump curve travels 1:1.
        // v19.39 — DO NOT `.start(0)` here. The Loop is scheduled on
        // the transport, and `play()` calls `T.cancel(0)` right after
        // hydration to clear any leftover schedulers — which would
        // silently nuke this Loop before playback begins. Instead we
        // stash the un-started Loop on the gain node so `play()` (and
        // the offline renderer) can `.start(0)` it AFTER `T.cancel(0)`.
        // That fix is what makes Crow Pumper on melodic tracks (piano,
        // pads) actually duck during generator playback + export.
        const gain = new Tone.Gain(1);
        gain._pumpDepth = Math.max(0, Math.min(1, params.amount ?? 0.6));
        const modeMap = { "1/2": "2n", "1/4": "4n", "1/8": "8n" };
        const rawMode = params.mode || "1/4";
        gain._pumpMode = modeMap[rawMode] || (typeof rawMode === "string" && /^\d+n$/.test(rawMode) ? rawMode : "4n");
        gain._pumpLoop = new Tone.Loop((time) => {
          const d = gain._pumpDepth;
          const floor = Math.max(0.02, 1 - d);
          const sub = Tone.Time(gain._pumpMode).toSeconds();
          gain.gain.cancelScheduledValues(time);
          gain.gain.setValueAtTime(1, time);
          gain.gain.linearRampToValueAtTime(floor, time + 0.005);
          gain.gain.linearRampToValueAtTime(1, time + Math.max(0.02, sub * 0.85));
        }, gain._pumpMode);
        // Marker read by _startFxLoops() in play() + renderToBuffer().
        gain._isPumper = true;
        return gain;
      }
      case "delay": {
        const rawTime = params.time;
        // Accept Tone notation ("4n","8n") from BPM-sync AND legacy numeric seconds.
        const t = typeof rawTime === "string" ? rawTime : (typeof rawTime === "number" ? rawTime : "8n");
        const d = new Tone.FeedbackDelay(t, params.feedback ?? 0.3);
        d.wet.value = params.wet ?? 0.3;
        return d;
      }
      case "reverb":
        return new Tone.Reverb({ decay: params.decay ?? 2.5, preDelay: 0.01, wet: params.wet ?? 0.3 });
      case "compressor":
        return new Tone.Compressor({ threshold: params.threshold ?? -20, ratio: params.ratio ?? 4, attack: 0.01, release: 0.1 });
      case "limiter":
        return new Tone.Limiter(params.threshold ?? -1);
      case "distortion":
        return new Tone.Distortion({ distortion: params.amount ?? 0.3, wet: params.wet ?? 0.5 });
      case "eq": {
        // 5-band parametric EQ. Mirrors the DAW's wrapped-gain structure so
        // params travel 1:1. If the saved bands are missing, this stays flat.
        const input = new Tone.Gain(1);
        const output = new Tone.Gain(1);
        const bandDefs = params.bands || [
          { freq: 80,    gain: 0, q: 0.71, type: "lowshelf"  },
          { freq: 250,   gain: 0, q: 1.0,  type: "peaking"   },
          { freq: 1000,  gain: 0, q: 1.0,  type: "peaking"   },
          { freq: 4000,  gain: 0, q: 1.0,  type: "peaking"   },
          { freq: 10000, gain: 0, q: 0.71, type: "highshelf" },
        ];
        const filters = bandDefs.map((b) => new Tone.Filter({
          type: b.type || "peaking", frequency: b.freq || 1000,
          gain: b.gain || 0, Q: b.q ?? 1,
        }));
        input.chain(...filters, output);
        output.input = input;
        return output;
      }
      // saturation + waveview intentionally skipped — waveview is a
      // visualizer-only passthrough, saturation uses Tone.Chebyshev which
      // is complex to parity-match here and doesn't materially affect the
      // House pump-preview use case.
      default:
        return null;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[buildGenFxNode] failed", plugin, e);
    return null;
  }
}

// v12.1 — Assemble the full signal chain for one component:
//   instrument → panner → fxChain[0] → fxChain[1] → ... → volume → dest
// Instrument is a Tone.Sampler when the source track had a sampleUrl,
// or a Tone.PolySynth reconstructed from track.synthConfig when the
// source was a Synth/Melody track without a sample. Returns
// { instrument, trigger, dispose, nodes } so the caller can teardown
// safely on the next Generate click.
function buildGenVoice(role, comp, dest) {
  const t = comp?.track;
  if (!t) return null;
  try {
    const buffer = comp._buffer;                    // pre-decoded by _hydrateComponents
    const rootPitch = t.samplerRootPitch || (role === "bass" ? "C1" : role === "melody" ? "C4" : "C3");
    // Build the instrument. Prefer sampler path — matches how the DAW
    // plays these components — falling back to a synth when only a
    // synthConfig is available.
    let instrument;
    let instrumentKind = "sampler";
    if (buffer) {
      instrument = new Tone.Sampler({ urls: { [rootPitch]: buffer } });
      // v12.0 glide behaviour applies to synths only; sampler ignores portamento.
    } else if (t.synthConfig || t.type === "synth" || t.type === "melody" || t.type === "bass") {
      const cfg = t.synthConfig || {};
      const env = cfg.envelope || { attack: 0.02, decay: 0.25, sustain: 0.55, release: 1.1 };
      const oscType = cfg.oscType || "fatsawtooth";
      const glide = Math.max(0, Math.min(4, t.glide || 0));
      instrument = new Tone.PolySynth(Tone.Synth, {
        oscillator: oscType === "fatsawtooth" ? { type: "fatsawtooth", count: 3, spread: 30 } : { type: oscType },
        envelope: env,
        portamento: glide,
      });
      // Match DAW: glide > 0 forces mono voice-stealing so pitch actually ramps.
      try { if (glide > 0) instrument.maxPolyphony = 1; } catch { /* noop */ }
      instrumentKind = "synth";
    } else {
      return null;
    }

    // Build the FX chain in order.
    const fxNodes = [];
    for (const fx of (t.fxChain || [])) {
      const node = buildGenFxNode(fx.plugin, fx.params || {});
      if (node) fxNodes.push(node);
    }

    // Pan + volume tail. Tone.Panner's pan is -1..1 which matches the DAW store.
    const panner = new Tone.Panner(Math.max(-1, Math.min(1, t.pan ?? 0)));
    // track.volume is a linear 0..1 in the store — convert to dB for
    // Tone.Volume so the mix balance survives.
    const volDb = typeof t.volume === "number"
      ? 20 * Math.log10(Math.max(0.001, t.volume))
      : 0;
    const volume = new Tone.Volume(volDb);

    // Wire: instrument → panner → fx0 → fx1 → ... → volume → dest
    // Note: Tone chains through `.chain(...)`. For our custom EQ wrapper
    // we use its synthetic .input, which `connect()` picks up automatically.
    const chain = [instrument, panner, ...fxNodes, volume, dest];
    for (let i = 0; i < chain.length - 1; i++) {
      try { chain[i].connect(chain[i + 1]); } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[buildGenVoice] connect failed at index", i, e);
      }
    }

    const dispose = () => {
      try { instrument.dispose?.(); } catch { /* noop */ }
      try { panner.dispose?.(); } catch { /* noop */ }
      try { volume.dispose?.(); } catch { /* noop */ }
      for (const n of fxNodes) {
        // v19.39 — Stop + dispose pumper's Tone.Loop first so its
        // scheduled callbacks don't fire against a disposed gain node
        // (previously a rapid re-Generate leaked ghost pumper Loops
        // that kept modulating a dead gain — audible glitches).
        try { n._pumpLoop?.stop?.(); } catch { /* noop */ }
        try { n._pumpLoop?.dispose?.(); } catch { /* noop */ }
        try { n.dispose?.(); } catch { /* noop */ }
      }
    };

    return { instrument, rootPitch, kind: instrumentKind, fxNodes, panner, volume, dispose };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[buildGenVoice] failed", role, e);
    return null;
  }
}

// Legacy adapter kept for external callers; wraps the v12.1 builder.
function makeComponentVoice(buffer, rootPitch, dest) {
  if (!buffer) return null;
  try {
    return {
      sampler: new Tone.Sampler({ urls: { [rootPitch]: buffer } }).connect(dest),
      rootPitch,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[GenPlayer] failed to build component voice", e);
    return null;
  }
}

class GenPlayer {
  constructor() {
    this.synths = null;
    this.pattern = null;
    this.bpm = 120;
    this.repeatId = null;
    this.stepIndex = 0;
    this.subscribers = new Set();
    // Per-component voice cache — rebuilt whenever loadPattern receives a
    // new pattern with a different `components` map. Keyed by role.
    this.componentVoices = {};   // { kick: {sampler, rootPitch}, ... }
    this.masterBus = null;       // Tone.EQ3 → destination
  }

  async init() {
    if (this.synths) return;
    await Tone.start();
    const dest = Tone.getDestination();
    const eq = new Tone.EQ3({ low: 2, mid: 0, high: 1 }).connect(dest);
    this.masterBus = eq;
    // Master analyzer used by the sleek waveform visualizer.
    this.masterWaveform = new Tone.Waveform(1024);
    this.masterMeter = new Tone.Meter({ smoothing: 0.85, normalRange: false });
    dest.connect(this.masterWaveform);
    dest.connect(this.masterMeter);

    // Fallback synths — only used when the interchange left a component
    // slot empty (cold-start). Producers with saved projects will always
    // hit the per-component Sampler path instead.
    this.synths = {
      kick:  new Tone.MembraneSynth({ octaves: 6, envelope: { attack: 0, decay: 0.25, sustain: 0.8, release: 0 } }).connect(eq),
      snare: new Tone.NoiseSynth({ noise: { type: "white" }, envelope: { attack: 0, decay: 0.12, sustain: 0.8, release: 0 } }).connect(eq),
      hihat: new Tone.MetalSynth({ frequency: 250, envelope: { attack: 0, decay: 0.06, sustain: 0.8, release: 0 }, harmonicity: 5.1, modulationIndex: 32, resonance: 4000, octaves: 1.5 }).connect(eq),
      clap:  new Tone.NoiseSynth({ noise: { type: "pink" }, envelope: { attack: 0, decay: 0.1, sustain: 0.8, release: 0 } }).connect(eq),
      bass:  new Tone.MonoSynth({ oscillator: { type: "sawtooth" }, filter: { Q: 4, type: "lowpass", rolloff: -24 }, envelope: { attack: 0.005, decay: 0.2, sustain: 0.6, release: 0.05 }, filterEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.3, release: 0.1, baseFrequency: 60, octaves: 4 } }).connect(eq),
      lead:  new Tone.PolySynth(Tone.Synth, { oscillator: { type: "fatsawtooth", count: 3, spread: 25 }, envelope: { attack: 0.02, decay: 0.25, sustain: 0.55, release: 0.15 } }).connect(eq),
    };
    this.synths.hihat.volume.value = -15;
    this.synths.clap.volume.value = -6;
    this.synths.bass.volume.value = -8;
    this.synths.lead.volume.value = -10;
    // Kick off admin default kit loads in the background (fallback only).
    drumKitManager.refresh().catch(() => {});
    bassKitManager.refresh().catch(() => {});
    melodyKitManager.refresh().catch(() => {});
  }

  // v9.9 — Rebuild the per-component signal chain from the pattern's
  // `components` map. v12.1: NOW includes the full FX chain (Crow Pumper,
  // Delay, Reverb, EQ...), panner, glide, and volume — plus a synth
  // fallback when only a synthConfig (no sample) travels with the
  // component. So a House pad with a 1/4 Crow Pumper sounds IDENTICAL
  // between the DAW authoring session and the /api/ai/generate preview.
  async _hydrateComponents(pattern) {
    // Dispose previous voices (instrument + fx + panner + volume) so
    // repeated Generate clicks don't leak Tone.Loop pumper schedules.
    for (const v of Object.values(this.componentVoices)) {
      try { v?.dispose?.(); } catch { /* noop */ }
    }
    this.componentVoices = {};
    const comps = pattern?.components || {};
    const dest = this.masterBus || Tone.getDestination();

    // Pre-decode every sampleUrl in parallel FIRST so the synchronous
    // signal-chain build (Tone.Sampler needs the buffer up-front) can
    // run without awaiting inside the loop. Synth-only components skip
    // this step because they have no buffer to decode.
    const decodes = [];
    for (const role of ["kick", "snare", "hihat", "clap", "bass", "melody"]) {
      const c = comps[role];
      const url = c?.track?.sampleUrl;
      if (!url) continue;
      decodes.push(
        decodeSampleUrl(url).then((buf) => { if (c) c._buffer = buf; })
                            .catch(() => {})
      );
    }
    await Promise.all(decodes);

    // Now build the full voice per role. Handles both sampler + synth
    // paths + FX chain reconstruction.
    for (const role of ["kick", "snare", "hihat", "clap", "bass", "melody"]) {
      const c = comps[role];
      if (!c || !c.track) continue;
      const voice = buildGenVoice(role, c, dest);
      if (voice) this.componentVoices[role] = voice;
    }
    // Explicit gate — any residual Tone AudioBuffer decoding must finish
    // before the transport fires the first step.
    try { await Tone.loaded(); } catch { /* noop */ }
  }

  async loadPattern(pattern, bpm = 120) {
    await this.init();
    // v21.1 — HARD TEAR-DOWN before hydration (Feb 2026 static/glitch
    // fix). Rapid re-generation used to overlap the previous playback
    // with the new hydration: old scheduleRepeat callbacks fired
    // against half-disposed voices while new samples were still
    // decoding, producing audible crackle/static. We now:
    //   1) Fully stop the transport + evict every scheduled callback
    //      BEFORE touching componentVoices.
    //   2) Bump a hydration token so any in-flight decode from the
    //      previous loadPattern call gets ignored on completion.
    //   3) Set the transport BPM only after tear-down so the new
    //      loop points resolve against the fresh tempo.
    try { this.stop(); } catch { /* noop */ }
    this._hydrationToken = (this._hydrationToken || 0) + 1;
    const token = this._hydrationToken;
    this.pattern = null; // gate play() until hydration finishes
    this.bpm = bpm;
    Tone.getTransport().bpm.value = bpm;
    // Hydrate per-component sounds AND admin default kits in parallel so
    // the fallback path is ready before we return.
    await Promise.all([
      this._hydrateComponents(pattern),
      drumKitManager.refresh().catch(() => {}),
      bassKitManager.refresh().catch(() => {}),
      melodyKitManager.refresh().catch(() => {}),
    ]);
    // A newer loadPattern started after us — abandon.
    if (token !== this._hydrationToken) return;
    this.pattern = pattern;
  }

  isReady() { return !!this.pattern && !!this.synths; }

  subscribe(fn) { this.subscribers.add(fn); return () => this.subscribers.delete(fn); }
  _emit() { this.subscribers.forEach((f) => f(this.stepIndex)); }

  async play() {
    if (!this.pattern) return;
    await this.init();
    // Belt-and-braces: ensure the AudioContext is running (must be resumed
    // from a user gesture) AND every audio buffer used by the render is
    // fully decoded. Without these, the FIRST step of a fresh page load
    // can fire before Tone finishes attaching buffers → silent playback.
    await Tone.start();
    try { await Tone.loaded(); } catch { /* noop */ }
    const T = Tone.getTransport();
    // v16.2 HARD-FIX per user spec: fully reset transport before every
    // fresh generation. stop() halts + rewinds; cancel(0) evicts every
    // previously-scheduled callback (leftover scheduleRepeat ids from
    // prior generations would otherwise fire silently against disposed
    // voices). Also nuke our own repeatId so a rapid double-tap doesn't
    // register two schedulers.
    try { if (this.repeatId != null) T.clear(this.repeatId); } catch { /* noop */ }
    this.repeatId = null;
    T.stop();
    T.cancel(0);
    T.position = "0:0:0";
    // Set BPM BEFORE loop points so "2m" resolves to the correct seconds.
    T.bpm.value = this.bpm;
    T.loop = true;
    T.loopStart = 0;
    // v16.3 STABLE REVERT — 1-bar loop (16 steps). Music that only has
    // 1 bar of content now loops cleanly instead of drifting mid-cycle.
    T.loopEnd = "1m";
    // v19.39 — Start every FX-chain Tone.Loop (Crow Pumper) AFTER
    // T.cancel(0) so it isn't wiped out. Without this the pumper
    // Loop was scheduled during _hydrateComponents(), then nuked by
    // cancel(0) right before playback — insert FX on melodic tracks
    // (piano/pad with pumper) rendered dry. Drum-track FX with
    // no scheduler (delay/reverb/EQ) were unaffected, which is why
    // the bug looked "melodic-only". Idempotent: calling start(0)
    // on an already-stopped Loop simply re-arms it.
    for (const voice of Object.values(this.componentVoices)) {
      for (const n of (voice?.fxNodes || [])) {
        if (n && n._isPumper && n._pumpLoop) {
          try { n._pumpLoop.stop(); } catch { /* noop */ }
          try { n._pumpLoop.start(0); } catch { /* noop */ }
        }
      }
    }
    const p = this.pattern;
    const secPerStep = 60 / this.bpm / 4;
    this.stepIndex = 0;

    // Per-role trigger — component sampler first (1:1 source sound), then
    // admin default kit, then synth fallback. Drum triggers convert the
    // 16-step boolean grid to Sampler note-triggers at the sampler's
    // rootPitch, mirroring exactly what the DAW Sampler + Piano Roll
    // hydration does on Edit-in-DAW so the two paths sound identical.
    // v21.1 — STATIC/GLITCH + SILENT-MELODY FIX (Feb 2026).
    // Previous v19.6.2 strict-R&B path silenced any lane whose component
    // sampler failed to hydrate. In practice this broke R&B melody
    // playback whenever the melody track's sampleUrl 404'd (host mismatch
    // between preview + prod) OR the track had only a synthConfig — the
    // producer saw the melody in the piano roll but heard nothing.
    // Restored fallback chain: component sampler → admin default kit →
    // built-in synth. Music is always better than silence.
    const triggerDrum = (role, time, vel, fallbackKitSlot, synthFallback, dur = "8n") => {
      const voice = this.componentVoices[role];
      if (voice?.instrument) {
        try {
          voice.instrument.triggerAttackRelease(voice.rootPitch, dur, time, vel);
          return;
        } catch { /* fall through */ }
      }
      if (fallbackKitSlot && drumKitManager.trigger(fallbackKitSlot, time, vel)) return;
      synthFallback(time, vel);
    };
    const triggerMelodic = (role, pitch, dur, time, vel, kitManager, synthFallback) => {
      const voice = this.componentVoices[role];
      if (voice?.instrument) {
        try {
          voice.instrument.triggerAttackRelease(pitch, dur, time, vel);
          return;
        } catch { /* fall through */ }
      }
      if (kitManager && kitManager.trigger(pitch, time, dur, vel)) return;
      synthFallback(time, vel);
    };

    this.repeatId = T.scheduleRepeat((time) => {
      // v16.3 STABLE REVERT — 16 steps per loop (1 bar). Derive
      // stepIndex from Tone.Transport.progress (a normalized 0..1
      // across the current loop range) — mathematically impossible
      // to drift across loop wraps.
      const progress = Math.max(0, Math.min(0.9999, T.progress));
      const s = Math.floor(progress * 16) % 16;
      this.stepIndex = s;
      try {
        // v9.9.5 — HIGH-RES drum triggering. Prefer the fractional-safe
        // `drum_notes` arrays (each entry: {step, duration, velocity, pitch?})
        // so 1/64 hi-hat rolls and off-grid ghost hits play at their EXACT
        // source positions. Fall back to the legacy 16-step boolean lane
        // only when drum_notes is empty (older curated_projects rows).
        const dn = p.drum_notes || {};
        const fireDrumRole = (role, fallbackKitSlot, synthFallback) => {
          const notes = dn[role];
          if (Array.isArray(notes) && notes.length) {
            notes.filter((n) => Math.floor(Number(n.step) || 0) === s).forEach((n) => {
              const off = ((Number(n.step) || 0) - s) * secPerStep;
              const dur = Math.max(0.02, (Number(n.duration) || 0.25) * secPerStep);
              const vel = typeof n.velocity === "number" ? Math.max(0.05, Math.min(1, n.velocity)) : 1;
              // v19.6.2 — Forward the note's real duration so long one-shots
              // (Crash Reverse @ step 0) ring fully instead of being cut
              // to 8n by triggerDrum's default.
              triggerDrum(role, time + off, vel, fallbackKitSlot, synthFallback, dur);
            });
            return;
          }
          // Legacy path — 16-step boolean lane.
          if (p.drums?.[role]?.[s]) triggerDrum(role, time, role === "hihat" ? 0.6 : (role === "snare" ? 0.9 : (role === "clap" ? 0.8 : 1.0)), fallbackKitSlot, synthFallback);
        };
        fireDrumRole("kick",  "kick",  (t, v) => this.synths.kick.triggerAttackRelease("C1", "8n", t, v));
        fireDrumRole("snare", "snare", (t, v) => this.synths.snare.triggerAttackRelease("16n", t, v));
        fireDrumRole("hihat", "hihat", (t, v) => this.synths.hihat.triggerAttackRelease("C4", "32n", t, v));
        fireDrumRole("clap",  "clap",  (t, v) => this.synths.clap.triggerAttackRelease("16n", t, v));
        (p.bass || []).filter((n) => Math.floor(Number(n.step) || 0) === s).forEach((n) => {
          const dur = Math.max(0.05, (n.duration || 1) * secPerStep);
          const off = ((Number(n.step) || 0) - s) * secPerStep;
          triggerMelodic("bass", n.pitch, dur, time + off, 0.9, bassKitManager, (t, v) => this.synths.bass.triggerAttackRelease(n.pitch, dur, t, v));
        });
        (p.melody || []).filter((n) => Math.floor(Number(n.step) || 0) === s).forEach((n) => {
          const dur = Math.max(0.05, (n.duration || 1) * secPerStep);
          const off = ((Number(n.step) || 0) - s) * secPerStep;
          triggerMelodic("melody", n.pitch, dur, time + off, 0.85, melodyKitManager, (t, v) => this.synths.lead.triggerAttackRelease(n.pitch, dur, t, v));
        });
      } catch { /* noop */ }
      this._emit();
      // v16.0 — stepIndex is now derived from Transport position at
      // the top of the callback, so no manual increment is needed.
    }, "16n");
    T.start();
  }

  stop() {
    const T = Tone.getTransport();
    T.stop();
    T.cancel(0);
    if (this.repeatId !== null) { try { T.clear(this.repeatId); } catch { /* noop */ } this.repeatId = null; }
    this.stepIndex = 0;
    this._emit();
  }

  getWaveform() { return this.masterWaveform?.getValue() ?? null; }
  getLevelDb() {
    const v = this.masterMeter?.getValue();
    return typeof v === "number" && Number.isFinite(v) ? v : -Infinity;
  }

  // Render a ~2-minute loop of the current pattern to an offline AudioBuffer.
  // v9.9 — also honours the per-component sound assignments so exported
  // WAVs match the live playback + DAW hydration.
  async renderToBuffer({ sampleRate = 44100, target_seconds = 120 } = {}) {
    if (!this.pattern) throw new Error("Nothing to render — generate a beat first.");
    const p = this.pattern;
    const bpm = this.bpm || 120;
    const secPerBar = (60 / bpm) * 4;
    const secPerStep = 60 / bpm / 4;
    const bars = Math.max(4, Math.ceil(target_seconds / secPerBar));
    const totalSec = bars * secPerBar + 0.4;

    // Pre-decode every component sampleUrl on the online context (offline
    // decoder is stricter and can reject WAVs the live context accepts).
    const comps = p.components || {};
    const compBuffers = {};
    const compRoots = {};
    for (const role of ["kick", "snare", "hihat", "clap", "bass", "melody"]) {
      const c = comps[role];
      const url = c?.track?.sampleUrl;
      if (!url) continue;
      const buf = await decodeSampleUrl(url);
      if (buf) {
        compBuffers[role] = buf;
        compRoots[role] = c?.track?.samplerRootPitch || (role === "bass" ? "C1" : role === "melody" ? "C4" : "C3");
      }
    }
    // Admin default kits for the fallback path.
    await drumKitManager.refresh().catch(() => {});
    await bassKitManager.refresh().catch(() => {});
    await melodyKitManager.refresh().catch(() => {});
    const drumUrlMap = drumKitManager.getSampleUrls();
    const bassUrlMap = bassKitManager.getSampleUrls();
    const melodyMeta = melodyKitManager.getActiveSampleUrl();
    const drumBuffers = {};
    for (const [k, url] of Object.entries(drumUrlMap || {})) {
      const b = await decodeSampleUrl(url);
      if (b) drumBuffers[k] = b;
    }
    const bassBuffers = {};
    for (const [k, url] of Object.entries(bassUrlMap || {})) {
      const b = await decodeSampleUrl(url);
      if (b) bassBuffers[k] = b;
    }
    const melodyBuffer = melodyMeta ? await decodeSampleUrl(melodyMeta.url) : null;

    const events = { kick: [], snare: [], hihat: [], clap: [], bass: [], melody: [] };
    // v19.6.2 — Prefer high-res drum_notes so long one-shots (Crash Reverse
    // @ step 0 in the snare slot) ring out to their real duration instead
    // of being clamped by the 16-step boolean lane's fixed 8n/16n.
    const dn = p.drum_notes || {};
    for (let b = 0; b < bars; b++) {
      const barOffset = b * secPerBar;
      const drumRoles = ["kick", "snare", "hihat", "clap"];
      for (const role of drumRoles) {
        const notes = dn[role];
        if (Array.isArray(notes) && notes.length) {
          notes.forEach((n) => {
            const step = Number(n.step) || 0;
            const t = barOffset + step * secPerStep;
            const dur = Math.max(0.02, (Number(n.duration) || 0.25) * secPerStep);
            const vel = typeof n.velocity === "number" ? Math.max(0.05, Math.min(1, n.velocity)) : (role === "hihat" ? 0.6 : role === "clap" ? 0.8 : 0.9);
            events[role].push({ t, dur, vel });
          });
        } else {
          for (let s = 0; s < 16; s++) {
            if (p.drums?.[role]?.[s]) {
              const t = barOffset + s * secPerStep;
              const dur = role === "kick" ? "8n" : role === "hihat" ? "32n" : "16n";
              const vel = role === "hihat" ? 0.6 : role === "clap" ? 0.8 : 0.9;
              events[role].push({ t, dur, vel });
            }
          }
        }
      }
      (p.bass || []).forEach((n) => {
        const t = barOffset + (Number(n.step) || 0) * secPerStep;
        const dur = Math.max(0.05, (Number(n.duration) || 1) * secPerStep);
        events.bass.push({ t, pitch: n.pitch, dur, vel: 0.9 });
      });
      (p.melody || []).forEach((n) => {
        const t = barOffset + (Number(n.step) || 0) * secPerStep;
        const dur = Math.max(0.05, (Number(n.duration) || 1) * secPerStep);
        events.melody.push({ t, pitch: n.pitch, dur, vel: 0.85 });
      });
    }
    for (const k of Object.keys(events)) events[k].sort((a, b) => a.t - b.t);

    const toneBuffer = await Tone.Offline(({ transport }) => {
      // v19.39 — Offline transport must be set + started so any Tone.Loop
      // scheduled on it (Crow Pumper's rhythmic duck) actually fires
      // during the render. Without this the pumper Loop never runs in
      // the offline context → exported WAV has zero ducking on melodic
      // tracks (piano/pad), which is exactly the bug being fixed.
      transport.bpm.value = bpm;
      const out = new Tone.EQ3({ low: 1.5, mid: 0, high: 0.8 }).toDestination();
      // v19.39 — Per-component FULL SIGNAL CHAIN mirroring the live
      // playback path. Previously every component's Sampler was
      // connected straight to `out`, silently dropping the fxChain
      // (Crow Pumper, EQ, Delay, Reverb...), panner, and per-track
      // volume. So exports were dry — Crow Pumper ducking on a piano
      // never made it into the WAV. Now we build:
      //   Sampler → panner → fx0 → fx1 → ... → volume → out
      // per role, exactly like buildGenVoice() does online.
      const pumperLoops = [];
      const compSamplers = {};
      const buildCompChain = (role) => {
        const t = comps[role]?.track;
        const buf = compBuffers[role];
        if (!t || !buf) return null;
        let instrument;
        try {
          instrument = new Tone.Sampler({ urls: { [compRoots[role]]: buf } });
        } catch {
          return null;
        }
        const fxNodes = [];
        for (const fx of (t.fxChain || [])) {
          const node = buildGenFxNode(fx.plugin, fx.params || {});
          if (node) fxNodes.push(node);
        }
        const panner = new Tone.Panner(Math.max(-1, Math.min(1, t.pan ?? 0)));
        const volDb = typeof t.volume === "number"
          ? 20 * Math.log10(Math.max(0.001, t.volume))
          : 0;
        const volume = new Tone.Volume(volDb);
        const chain = [instrument, panner, ...fxNodes, volume, out];
        for (let i = 0; i < chain.length - 1; i++) {
          try { chain[i].connect(chain[i + 1]); } catch { /* noop */ }
        }
        for (const n of fxNodes) {
          if (n && n._isPumper && n._pumpLoop) {
            try { n._pumpLoop.start(0); } catch { /* noop */ }
            pumperLoops.push(n._pumpLoop);
          }
        }
        return instrument;
      };
      for (const role of Object.keys(compBuffers)) {
        const inst = buildCompChain(role);
        if (inst) compSamplers[role] = inst;
      }
      // v19.39 — Kick the offline transport so scheduled Tone.Loops
      // (pumper) fire during the render.
      try { transport.start(0); } catch { /* noop */ }
      // Fallback voices for any role missing a component. These do NOT
      // route through the component's fxChain (there IS no component),
      // so they hit `out` directly — matches prior behaviour.
      const drumPlayers = {};
      for (const slot of ["kick", "snare", "hihat", "clap"]) {
        if (drumBuffers[slot]) {
          try { drumPlayers[slot] = new Tone.Player(drumBuffers[slot]).connect(out); } catch { /* skip */ }
        }
      }
      const kickSynth  = new Tone.MembraneSynth({ octaves: 6, envelope: { attack: 0.001, decay: 0.35, sustain: 0.01, release: 1.4 } }).connect(out);
      const snareSynth = new Tone.NoiseSynth({ noise: { type: "white" }, envelope: { attack: 0.001, decay: 0.18, sustain: 0 } }).connect(out);
      const hihatSynth = new Tone.MetalSynth({ envelope: { attack: 0.001, decay: 0.08, release: 0.01 }, harmonicity: 5.1, modulationIndex: 32, resonance: 4000, octaves: 1.5 }).connect(out);
      const clapSynth  = new Tone.NoiseSynth({ noise: { type: "pink" }, envelope: { attack: 0.002, decay: 0.16, sustain: 0 } }).connect(out);
      hihatSynth.volume.value = -15; clapSynth.volume.value = -6;
      let bassSampler = null;
      if (Object.keys(bassBuffers).length > 0) {
        try { bassSampler = new Tone.Sampler({ urls: bassBuffers }).connect(out); } catch { bassSampler = null; }
      }
      const bassSynth = new Tone.MonoSynth({
        oscillator: { type: "sawtooth" }, filter: { Q: 4, type: "lowpass", rolloff: -24 },
        envelope: { attack: 0.005, decay: 0.2, sustain: 0.6, release: 0.4 },
        filterEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.3, release: 0.4, baseFrequency: 60, octaves: 4 },
      }).connect(out);
      bassSynth.volume.value = -8;
      let leadSampler = null;
      if (melodyMeta && melodyBuffer) {
        try { leadSampler = new Tone.Sampler({ urls: { [melodyMeta.rootPitch]: melodyBuffer } }).connect(out); } catch { leadSampler = null; }
      }
      const leadSynth = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "fatsawtooth", count: 3, spread: 25 },
        envelope: { attack: 0.02, decay: 0.25, sustain: 0.55, release: 0.6 },
      }).connect(out);
      leadSynth.volume.value = -10;

      const fireRole = (role, e, oneShotFallback, synthFallback) => {
        const s = compSamplers[role];
        if (s) { try { s.triggerAttackRelease(e.pitch || compRoots[role], e.dur, e.t, e.vel); return; } catch { /* fall */ } }
        oneShotFallback && oneShotFallback();
        synthFallback && synthFallback();
      };
      events.kick.forEach((e)  => fireRole("kick",  e, () => { const p = drumPlayers.kick;  if (p) { try { p.volume.value = 0; p.start(e.t); return; } catch {} } kickSynth.triggerAttackRelease("C1", e.dur, e.t, e.vel); }));
      events.snare.forEach((e) => fireRole("snare", e, () => { const p = drumPlayers.snare; if (p) { try { p.volume.value = 0; p.start(e.t); return; } catch {} } snareSynth.triggerAttackRelease(e.dur, e.t, e.vel); }));
      events.hihat.forEach((e) => fireRole("hihat", e, () => { const p = drumPlayers.hihat; if (p) { try { p.volume.value = 0; p.start(e.t); return; } catch {} } hihatSynth.triggerAttackRelease("C4", e.dur, e.t, e.vel); }));
      events.clap.forEach((e)  => fireRole("clap",  e, () => { const p = drumPlayers.clap;  if (p) { try { p.volume.value = 0; p.start(e.t); return; } catch {} } clapSynth.triggerAttackRelease(e.dur, e.t, e.vel); }));
      events.bass.forEach((e)  => fireRole("bass",  e, () => { if (bassSampler) { try { bassSampler.triggerAttackRelease(e.pitch, e.dur, e.t, e.vel); return; } catch {} } bassSynth.triggerAttackRelease(e.pitch, e.dur, e.t, e.vel); }));
      events.melody.forEach((e) => fireRole("melody", e, () => { if (leadSampler) { try { leadSampler.triggerAttackRelease(e.pitch, e.dur, e.t, e.vel); return; } catch {} } leadSynth.triggerAttackRelease(e.pitch, e.dur, e.t, e.vel); }));
    }, totalSec, 2, sampleRate);

    if (toneBuffer && typeof toneBuffer.get === "function") {
      const native = toneBuffer.get();
      if (native) return native;
    }
    return toneBuffer;
  }
}

const genPlayer = new GenPlayer();
export default genPlayer;
